/**
 * weekUtils.ts
 * FEAT-2026-0007 — Pure BST (Saturday–Friday) week helpers for frontend use.
 *
 * BST = "BGC Schedule Time" — the project week runs Saturday→Friday,
 * matching BST timesheets (see CLAUDE.md § locked decisions).
 *
 * This extracts the week-arithmetic already present in vacationWeekSplits.ts
 * so that Phase2WeeklyGrid.tsx and any future calendar grids can reuse it
 * without duplicating the logic.
 *
 * All dates are ISO 'YYYY-MM-DD' strings. No Date objects leak out of these
 * functions (they accept/return strings only).
 */

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Add N calendar days to an ISO date string. */
function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Exported helpers
// ---------------------------------------------------------------------------

/**
 * Return the Saturday that starts the BST week containing `dateStr`.
 * If `dateStr` is already a Saturday, returns it unchanged.
 *
 * getUTCDay() → 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
 *
 * Days to subtract to reach the preceding Saturday:
 *   Sat(6) → 0   Sun(0) → 1   Mon(1) → 2   Tue(2) → 3
 *   Wed(3) → 4   Thu(4) → 5   Fri(5) → 6
 */
export function saturdayWeekStart(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const daysBack = dow === 6 ? 0 : dow + 1;
  return addDays(dateStr, -daysBack);
}

/**
 * Return the Friday that ends the BST week starting at `saturdayStr`.
 * Simple: Saturday + 6 days = Friday.
 */
export function weekEndFriday(saturdayStr: string): string {
  return addDays(saturdayStr, 6);
}

/**
 * Return an array of all Saturday week-starts whose week overlaps
 * the half-open date range [startDate, endDate] (both inclusive).
 *
 * Returns an empty array if startDate > endDate or either is empty.
 */
export function weekStartsInRange(startDate: string, endDate: string): string[] {
  if (!startDate || !endDate || startDate > endDate) return [];
  const starts: string[] = [];
  let cur = saturdayWeekStart(startDate);
  // Walk forward by 7-day increments. Include the week whose start ≤ endDate.
  while (cur <= endDate) {
    starts.push(cur);
    cur = addDays(cur, 7);
  }
  return starts;
}

/**
 * Check whether a [weekStart, weekStart+6] window overlaps a date range
 * [rangeStart, rangeEnd] (all inclusive comparisons on ISO strings).
 */
export function weekOverlapsRange(
  weekStart: string,
  rangeStart: string,
  rangeEnd: string,
): boolean {
  const weekEnd = weekEndFriday(weekStart);
  return weekStart <= rangeEnd && weekEnd >= rangeStart;
}

// ---------------------------------------------------------------------------
// Working-day helpers (weekend + stat-holiday aware) — 2026-06-25
//
// Run Mode Phase C: these are ATTENDED-mode helpers. Booking-card labels for
// CONTINUOUS (24/7) bookings must NOT snap to working days — a Saturday end
// is real run-time. Use bookingLabelRange in utils/runModeRendering.ts, which
// branches per mode and falls back to these for attended bookings.
// ---------------------------------------------------------------------------

/** True when an ISO 'YYYY-MM-DD' is a weekend (Sat/Sun) or one of the holiday dates. */
export function isNonWorkingDay(iso: string, holidays?: ReadonlySet<string>): boolean {
  const [y, m, d] = iso.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun … 6=Sat
  return dow === 0 || dow === 6 || (holidays?.has(iso) ?? false);
}

/**
 * Walk forward from `start` (inclusive) to the first WORKING day, not past `end`.
 * If every day in [start, end] is non-working, returns `start` unchanged (so a
 * weekend-only segment collapses to its boundary rather than vanishing).
 */
export function firstWorkingDay(start: string, end: string, holidays?: string[]): string {
  const set = holidays && holidays.length ? new Set(holidays) : undefined;
  let cur = start;
  let guard = 0;
  while (cur <= end && isNonWorkingDay(cur, set) && guard < 31) {
    cur = addDays(cur, 1);
    guard += 1;
  }
  return cur > end ? start : cur;
}

/**
 * Walk backward from `end` (inclusive) to the last WORKING day, not before `start`.
 * If every day is non-working, returns `end` unchanged.
 */
export function lastWorkingDay(end: string, start: string, holidays?: string[]): string {
  const set = holidays && holidays.length ? new Set(holidays) : undefined;
  let cur = end;
  let guard = 0;
  while (cur >= start && isNonWorkingDay(cur, set) && guard < 31) {
    cur = addDays(cur, -1);
    guard += 1;
  }
  return cur < start ? end : cur;
}

/** Count WORKING days (weekend + holiday excluded) in [start, end] inclusive. */
export function countWorkingDays(start: string, end: string, holidays?: string[]): number {
  if (start > end) return 0;
  const set = holidays && holidays.length ? new Set(holidays) : undefined;
  let n = 0;
  let cur = start;
  let guard = 0;
  while (cur <= end && guard < 366) {
    if (!isNonWorkingDay(cur, set)) n += 1;
    cur = addDays(cur, 1);
    guard += 1;
  }
  return n;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Format 'YYYY-MM-DD' as 'MMM D' (e.g. 'May 3'). */
export function fmtShortDate(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${MONTHS_SHORT[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
}

/**
 * Format a BST week (Saturday start) as a MONDAY-anchored work-week range
 * 'MMM D – MMM D' (Mon–Fri) — schedulers think in Mon-start weeks, matching the
 * "Week of {Monday}" convention used everywhere else. The Saturday key is
 * unchanged; only the displayed range starts on the Monday (+2 days).
 * E.g. saturdayStr='2025-05-03' → 'May 5 – May 9'.
 */
export function fmtWeekRange(saturdayStr: string): string {
  const [y, m, d] = saturdayStr.split('-').map(Number);
  const monday = new Date(Date.UTC(y, m - 1, d + 2)).toISOString().slice(0, 10);
  const friday = weekEndFriday(saturdayStr);
  return `${fmtShortDate(monday)} – ${fmtShortDate(friday)}`;
}
